import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RECURRING_CHARGE_CONSENT_TEXT_REVISION } from "@wetalk/domain";
import { Button } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { openSubscriptionPayform, type SubscriptionCardFormView, type SubscriptionOperationView } from "../../../api/slice/billing-operation.js";
import type { BillingProfileView } from "../../../api/slice/billing-profile.js";
import type { OfferChoiceView } from "../../../api/slice/commercial-offer.js";
import type { BalanceSubscriptionView, SubscriptionQuoteView } from "../../../api/slice/subscription.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
import { formatMoney } from "../../../money.js";
import { Block, BlockNote, type Outcome } from "../../account-admin/page/organization-page.js";
import {
  clockText,
  extraRateValueOf,
  localisedOf,
  longDateText,
  minuteCountText,
  namesText,
  offerNameOf,
  rateLinesOf,
  refusalNeedsBillingDetails,
  refusalNeedsNewQuote,
  refusalTextOf,
  talkTimeText,
  vatTextOf,
} from "./plan-copy.js";
import "./purchase-terms-gateway.js";
import type { PurchaseTermsView } from "./purchase-terms-gateway.js";

/**
 * The plan quote — `/finance/plans?offer=<code>` (plan-020 **WAPP-PLAN**; design §32.6.2, §32.12.3 Q-5…Q-20, §32.17;
 * card-rules block 4.1; OD-13, OD-30).
 *
 * The quote is FROZEN by the server (`POST /v1/accounts/{id}/billing-quotes`) and every line here is read from its
 * detail: the first charge (net + VAT = total), the recurring charge on the anchor day from the first renewal "until
 * you cancel", the included minutes and calls at once, the extra-time state, the rates by kind with whose price each
 * is, the covered and uncovered workspaces, the notice sentence, how to cancel and the business-use line — all ABOVE
 * the pay button and before the card form opens.
 *
 * THE CONSENT (OD-30): an UNTICKED box beside the pay button; the button is disabled until it is ticked. The words the
 * person agrees to are this page's catalog lines, tied to `RECURRING_CHARGE_CONSENT_TEXT_REVISION` by
 * `./plan-copy.test.ts` (a hash of the EN and EL lines). A quote that names other words than this page's (an old page,
 * new terms) is not offered for payment: the page says to reload.
 *
 * THE CHARGE (ED-10): a purchase or an upgrade is always a NEW cardholder-present Payform charge with
 * `frequencyType: recurring` — `POST /v1/billing/payment-methods/subscription-form` gives the options (the server's
 * amount), the processor's form is mounted in the page (it asks for a mobile number before Pay enables, Q-17), its
 * `ctn_` token is sent with the confirm, and the operation's outcome is read until it settles: `completed` → the plan
 * section; `needs_reconciliation` → "We are checking your payment" (the payment worker owns it); a 3-D Secure page →
 * the browser goes there. A downgrade (nothing due) is confirmed without a card or a consent (Q-19, Q-20).
 */

const PAYFORM_ELEMENT_ID = "subscription-payform";
const SETTLED = new Set(["completed", "failed", "cancelled"]);

type Stage =
  | { readonly kind: "review" }
  | { readonly kind: "form"; readonly form: SubscriptionCardFormView }
  | { readonly kind: "waiting"; readonly operation: SubscriptionOperationView }
  /** The charge was refused (`message` null), or the processor's form reported an error (its own words). */
  | { readonly kind: "declined"; readonly message: string | null };

/** The confirm's Idempotency-Key: one per quote and card token, so a resend replays, a new card is a new attempt. */
export function subscriptionConfirmationKeyOf(billingQuoteId: string, cardToken: string | null): string {
  return (cardToken === null ? `subscription-${billingQuoteId}` : `subscription-${billingQuoteId}-${cardToken}`).slice(0, 128);
}

/** Whether the quote is an upgrade or a purchase (something is charged now) — else a downgrade, applied later. */
export function chargesNow(quote: SubscriptionQuoteView): boolean {
  return quote.detail.firstCharge !== null && quote.amountDueMinor > 0;
}

/** The person's own refusal sentence (Q-28) or the API's, with the remedy's link when there is one. */
export function RefusalLine(props: { readonly error: unknown; readonly otherwise: string; readonly onRequote?: () => void }): ReactNode {
  usePortalTranslation("subscription");
  const own = refusalTextOf(props.error);
  return (
    <p className="pl-error" role="alert">
      {own ?? apiProblemText(props.error, props.otherwise)}
      {refusalNeedsBillingDetails(props.error) ? (
        <>
          {" "}
          <Link to="/finance/documents">{portalText("subscription", "refusal.billing_details")}</Link>
        </>
      ) : null}
      {refusalNeedsNewQuote(props.error) && props.onRequote !== undefined ? (
        <>
          {" "}
          <button type="button" className="pl-link" onClick={props.onRequote}>
            {portalText("subscription", "refusal.review_again")}
          </button>
        </>
      ) : null}
    </p>
  );
}

/** Q-14: the business-use line, with the tax id as the billing profile records it. */
function businessLineOf(profile: BillingProfileView | null | undefined): string {
  if (profile === null || profile === undefined || profile.taxId === null) return portalText("subscription", "quote.business_unread");
  if (!profile.taxIdChecked) return portalText("subscription", "quote.business_outside");
  return portalText("subscription", "quote.business", {
    tax_id: profile.taxId,
    tax_status: profile.taxIdStatus === "valid" ? portalText("subscription", "quote.tax_status.valid") : portalText("subscription", "quote.tax_status.recorded"),
  });
}

/** Block 4.1 (Q-5…Q-14), from the frozen detail. Exported for the suite. */
export function QuoteTerms(props: { readonly quote: SubscriptionQuoteView; readonly offerName: string; readonly profile: BillingProfileView | null | undefined; readonly held: BalanceSubscriptionView | null }): ReactNode {
  usePortalTranslation("subscription");
  const { detail } = props.quote;
  const zone = detail.billingTimezone;
  const first = detail.firstCharge;
  const recurring = detail.recurringCharge;
  const treatment = first?.taxTreatment ?? null;
  const rate = first?.vatRatePermille ?? props.quote.taxRatePermille;
  const extraRate = extraRateValueOf(detail.resolvedTalkRate);
  const upgrade = props.held !== null && first !== null;
  const addedSecond = props.held === null ? 0 : Math.max(0, detail.includedTalkSecond - props.held.includedTalkSecondGranted);
  return (
    <div className="pl-terms-block" aria-label={portalText("subscription", "chooser.title")}>
      {first !== null && !upgrade ? (
        <p className="pl-line pl-line-lead">
          {portalText("subscription", "quote.today", {
            first_total: formatMoney(first.total),
            first_net: formatMoney(first.net),
            vat_text: vatTextOf(first.vat, treatment, rate),
            start_date: longDateText(detail.startAt, zone),
          })}
        </p>
      ) : null}
      {first !== null && upgrade ? (
        <p className="pl-line pl-line-lead">{portalText("subscription", "quote.upgrade", { prorated_total: formatMoney(first.total), added_minutes: talkTimeText(addedSecond) })}</p>
      ) : null}
      {first === null && detail.offer !== null ? (
        <p className="pl-line pl-line-lead">{portalText("subscription", "quote.downgrade", { new_plan: props.offerName, effective_date: longDateText(detail.startAt, zone) })}</p>
      ) : null}
      {recurring !== null && detail.renewalAnchorDay !== null && detail.firstRenewalAt !== null ? (
        <p className="pl-line">
          {portalText("subscription", "quote.recurring", {
            recurring_total: formatMoney(recurring.total),
            recurring_net: formatMoney(recurring.net),
            vat_text: vatTextOf(recurring.vat, treatment, rate),
            anchor_day: String(detail.renewalAnchorDay),
            first_renewal_date: longDateText(detail.firstRenewalAt, zone),
          })}
        </p>
      ) : null}
      <p className="pl-line">{portalText("subscription", "quote.included", { included_minutes: minuteCountText(detail.includedTalkSecond), conc: String(detail.channelUnitAfter) })}</p>
      <p className="pl-line">{detail.extraTimeEnabled && extraRate !== null ? portalText("subscription", "quote.extra_on", { rate: extraRate }) : portalText("subscription", "quote.extra_off")}</p>
      {detail.resolvedTalkRate.length > 0 ? (
        <div className="pl-line">
          <p className="pl-line-head">{portalText("subscription", "quote.rates_title")}</p>
          <ul className="pl-rate-list">
            {rateLinesOf(detail.resolvedTalkRate).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {detail.coveredWorkspace.length > 0 ? <p className="pl-line">{portalText("subscription", "quote.covered", { covered_workspaces: namesText(detail.coveredWorkspace.map((one) => one.name)) })}</p> : null}
      {detail.uncoveredWorkspace.length > 0 ? <p className="pl-line">{portalText("subscription", "quote.uncovered", { uncovered_workspaces: namesText(detail.uncoveredWorkspace.map((one) => one.name)) })}</p> : null}
      {detail.channelUnitBefore !== detail.channelUnitAfter ? <p className="pl-line">{portalText("subscription", "quote.concurrency", { before: String(detail.channelUnitBefore), after: String(detail.channelUnitAfter) })}</p> : null}
      {detail.overLimitVoiceAgent.length > 0 ? (
        <p className="pl-line pl-line-caution" role="status">
          {portalText("subscription", "quote.over_limit", {
            effective_date: longDateText(detail.startAt, zone),
            after_limit: String(detail.channelUnitAfter),
            voice_agents: namesText(detail.overLimitVoiceAgent.map((one) => one.name)),
          })}
        </p>
      ) : null}
      {recurring !== null ? (
        <>
          <p className="pl-line">
            {portalText("subscription", "quote.notice", { notice_days: String(detail.termsNoticeDay) })}
          </p>
          <p className="pl-line">{portalText("subscription", "quote.cancel_how")}</p>
          <p className="pl-line">{businessLineOf(props.profile)}</p>
        </>
      ) : null}
    </div>
  );
}

/** The purchase terms beside a quote (plan-014 PD-8), as the plan-014 page said them. */
function PurchaseTermsLine(props: { readonly terms: PurchaseTermsView; readonly ticked: boolean; readonly onTick: (ticked: boolean) => void }): ReactNode {
  usePortalTranslation("finance");
  const { terms } = props;
  const documents = (
    <>
      <a href={terms.termsUrl} target="_blank" rel="noreferrer">{portalText("finance", "m0305")}</a>{" "}{portalText("finance", "m0306")}{" "}
      <a href={terms.privacyUrl} target="_blank" rel="noreferrer">{portalText("finance", "m0307")}</a>{" "}
      {portalText("finance", "m0308", { value1: terms.revision })}
    </>
  );
  if (terms.accepted) return <p className="pl-note">{portalText("finance", "m0697")}{documents}.</p>;
  if (!terms.canAccept) return <p className="pl-note" role="status">{portalText("finance", "m0309")}{documents}{portalText("finance", "m0698")}</p>;
  return (
    <label className="pl-terms">
      <input type="checkbox" checked={props.ticked} onChange={(event) => props.onTick(event.currentTarget.checked)} />
      <span>{portalText("finance", "m0311")}{documents}{portalText("finance", "m0312")}</span>
    </label>
  );
}

export interface SubscriptionQuotePanelProps {
  readonly accountId: string;
  readonly billingAccountId: string;
  readonly offer: OfferChoiceView;
  readonly held: BalanceSubscriptionView | null;
  readonly onBack: () => void;
  readonly onDone: (outcome: Outcome) => void;
}

/** The quote for one offer, its terms, the consent, the card form and the outcome. */
export function SubscriptionQuotePanel(props: SubscriptionQuotePanelProps): ReactNode {
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const kind = props.held === null ? "subscription_purchase" : "subscription_change";
  const quote = useQuery({
    queryKey: ["subscription-quote", props.billingAccountId, props.offer.commercialRevisionId, kind] as const,
    queryFn: () => gateway.quoteSubscription(props.accountId, { kind, billingAccountId: props.billingAccountId, commercialRevisionId: props.offer.commercialRevisionId }),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });
  const profile = useQuery({ queryKey: ["billing-profile", props.accountId] as const, queryFn: () => gateway.readBillingProfile(props.accountId), retry: false });
  const readTerms = gateway.readPurchaseTerms;
  const acceptTerms = gateway.acceptPurchaseTerms;
  const terms = useQuery({
    queryKey: ["purchase-terms", props.accountId] as const,
    queryFn: (): Promise<PurchaseTermsView> => {
      if (readTerms === undefined) throw new Error("unavailable");
      return readTerms(props.accountId);
    },
    enabled: readTerms !== undefined,
    staleTime: 0,
  });
  const [consented, setConsented] = useState(false);
  const [termsTicked, setTermsTicked] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: "review" });
  const termsBlocking = readTerms !== undefined && (terms.data === undefined || (!terms.data.accepted && (!terms.data.canAccept || !termsTicked)));

  const frozen = quote.data;
  const offerName = offerNameOf(props.offer);
  const now = frozen !== undefined && chargesNow(frozen);
  // The words this page shows are the ones the quote asks consent to — else an old page would consent to new words.
  const wordsCurrent = frozen === undefined || !frozen.detail.consentRequired || frozen.detail.consentTextRevision === RECURRING_CHARGE_CONSENT_TEXT_REVISION;

  const settle = async (operation: SubscriptionOperationView): Promise<void> => {
    if (operation.nextActionUrl !== null) {
      globalThis.location.assign(operation.nextActionUrl);
      return;
    }
    if (operation.state === "completed") {
      await queryClient.invalidateQueries({ queryKey: ["balance-subscription"] });
      await queryClient.invalidateQueries({ queryKey: ["offer-choices"] });
      await queryClient.invalidateQueries({ queryKey: ["commercial-offers"] });
      const detail = frozen?.detail;
      const later = frozen !== undefined && !chargesNow(frozen) && detail !== undefined;
      props.onDone({
        tone: "done",
        text: () =>
          later
            ? portalText("subscription", "quote.changed_later", { date: longDateText(detail.startAt, detail.billingTimezone) })
            : props.held === null
              ? portalText("subscription", "quote.started")
              : portalText("subscription", "quote.changed_now"),
      });
      return;
    }
    if (operation.state === "failed" || operation.state === "cancelled") {
      setStage({ kind: "declined", message: null });
      return;
    }
    setStage({ kind: "waiting", operation });
  };

  // The revision this panel accepted already (opening the card form accepts it; the confirm then only names it).
  const acceptedHere = useRef<string | null>(null);
  const acceptTermsIfNeeded = async (buying: boolean): Promise<string | null> => {
    if (readTerms === undefined) return null;
    const current = terms.data;
    if (current === undefined) throw new Error(portalText("finance", "m0689"));
    // A downgrade buys nothing: it names the revision only when the organization accepted it already.
    if (!buying) return current.accepted || acceptedHere.current === current.revision ? current.revision : null;
    if (!current.accepted && acceptedHere.current !== current.revision) {
      if (!current.canAccept || acceptTerms === undefined) throw new Error(portalText("finance", "m0690"));
      if (!termsTicked) throw new Error(portalText("finance", "m0313"));
      await acceptTerms(props.accountId, current.revision);
      acceptedHere.current = current.revision;
    }
    return current.revision;
  };

  const confirming = useMutation({
    mutationFn: async (cardToken: string | null) => {
      if (frozen === undefined) throw new Error(portalText("finance", "m0688"));
      const termsRevision = await acceptTermsIfNeeded(chargesNow(frozen) || props.held === null);
      return gateway.confirmSubscriptionOperation(props.accountId, {
        billingQuoteId: frozen.billingQuoteId,
        idempotencyKey: subscriptionConfirmationKeyOf(frozen.billingQuoteId, cardToken),
        termsRevision,
        consent: frozen.detail.consentRequired ? { textRevision: RECURRING_CHARGE_CONSENT_TEXT_REVISION, agreed: true } : null,
        cardToken,
        kind: frozen.kind,
        amountDueMinor: frozen.amountDueMinor,
      });
    },
    onSuccess: settle,
  });

  const opening = useMutation({
    mutationFn: async () => {
      if (frozen === undefined) throw new Error(portalText("finance", "m0688"));
      await acceptTermsIfNeeded(true);
      return gateway.openSubscriptionCardForm({ billingQuoteId: frozen.billingQuoteId });
    },
    onSuccess: (form) => setStage({ kind: "form", form }),
  });

  const { mutate: confirmWith } = confirming;
  const formStage = stage.kind === "form" ? stage.form : null;
  useEffect(() => {
    if (formStage === null) return;
    let cancelled = false;
    openSubscriptionPayform(formStage, PAYFORM_ELEMENT_ID).then(
      (cardToken) => {
        if (!cancelled) confirmWith(cardToken);
      },
      (error: unknown) => {
        if (!cancelled) setStage({ kind: "declined", message: error instanceof Error ? error.message : null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [formStage, confirmWith]);

  const waitingOn = stage.kind === "waiting" ? stage.operation.operationId : null;
  const status = useQuery({
    queryKey: ["billing-operation", waitingOn ?? "none"] as const,
    queryFn: () => gateway.readBillingOperation(waitingOn ?? ""),
    enabled: waitingOn !== null,
    refetchInterval: (state) => (state.state.data !== undefined && SETTLED.has(state.state.data.state) ? false : 2000),
  });
  // Once the operation settles, say so once (the guard keeps a re-render from settling it twice).
  const settledOnce = useRef<string | null>(null);
  useEffect(() => {
    const read = status.data;
    if (read === undefined || !SETTLED.has(read.state) || settledOnce.current === read.operationId) return;
    settledOnce.current = read.operationId;
    void settle({ ...read, nextActionUrl: null });
  });

  const header = (
    <div className="pl-quote-head">
      <p className="op-block-title">{offerName}</p>
      {props.offer.presentation === null ? null : <p className="op-block-dek">{localisedOf(props.offer.presentation.tagline)}</p>}
    </div>
  );

  if (quote.isPending) {
    return (
      <Block title={portalText("subscription", "chooser.title")}>
        {header}
        <BlockNote>{portalText("subscription", "quote.loading")}</BlockNote>
      </Block>
    );
  }
  if (quote.isError || frozen === undefined) {
    return (
      <Block title={portalText("subscription", "chooser.title")}>
        {header}
        <RefusalLine error={quote.error} otherwise={portalText("finance", "m0694")} onRequote={() => void quote.refetch()} />
        <div className="pl-actions">
          <Button variant="secondary" onClick={props.onBack}>{portalText("subscription", "quote.back")}</Button>
        </div>
      </Block>
    );
  }

  const first = frozen.detail.firstCharge;
  const recurring = frozen.detail.recurringCharge;
  const problem = opening.isError ? opening.error : confirming.isError ? confirming.error : null;
  const busy = opening.isPending || confirming.isPending;
  return (
    <Block title={portalText("subscription", "chooser.title")}>
      {header}
      <QuoteTerms quote={frozen} offerName={offerName} profile={profile.data} held={props.held} />
      <p className="pl-note">{portalText("subscription", "quote.held_until", { time: clockText(frozen.expiresAt) })}</p>
      {terms.data !== undefined && (now || props.held === null) ? <PurchaseTermsLine terms={terms.data} ticked={termsTicked} onTick={setTermsTicked} /> : null}
      {!wordsCurrent ? (
        <p className="pl-error" role="alert">{portalText("subscription", "quote.stale_page")}</p>
      ) : null}

      {stage.kind === "form" ? (
        <div className="pl-payform">
          {stage.form.phoneRequired ? <p className="pl-note">{portalText("subscription", "quote.payform_note")}</p> : null}
          <div id={PAYFORM_ELEMENT_ID} />
          {confirming.isPending ? <p className="pl-note" role="status">{portalText("subscription", "quote.confirming")}</p> : null}
        </div>
      ) : null}
      {stage.kind === "waiting" ? <p className="pl-note" role="status">{portalText("subscription", "quote.checking")}</p> : null}
      {stage.kind === "declined" ? <p className="pl-error" role="alert">{stage.message ?? portalText("subscription", "quote.declined")}</p> : null}
      {problem !== null ? <RefusalLine error={problem} otherwise={portalText("finance", "m0694")} onRequote={() => void quote.refetch()} /> : null}

      {now && recurring !== null && first !== null && stage.kind === "review" ? (
        <>
          <label className="pl-consent">
            <input type="checkbox" checked={consented} disabled={!wordsCurrent} onChange={(event) => setConsented(event.currentTarget.checked)} />
            <span>{portalText("subscription", "quote.consent", { recurring_total: formatMoney(recurring.total) })}</span>
          </label>
          {stage.kind === "review" ? <p className="pl-note">{portalText("subscription", "quote.payform_note")}</p> : null}
        </>
      ) : null}

      <div className="pl-actions">
        <Button variant="ghost" disabled={busy} onClick={props.onBack}>{portalText("subscription", "quote.back")}</Button>
        {stage.kind === "declined" ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConsented(false);
              setStage({ kind: "review" });
              void quote.refetch();
            }}
          >
            {portalText("subscription", "quote.try_again")}
          </Button>
        ) : null}
        {stage.kind === "review" && now && first !== null ? (
          <Button variant="primary" disabled={!consented || !wordsCurrent || busy || termsBlocking} onClick={() => opening.mutate()}>
            {props.held === null ? portalText("subscription", "quote.pay", { first_total: formatMoney(first.total) }) : portalText("subscription", "quote.pay_upgrade", { first_total: formatMoney(first.total) })}
          </Button>
        ) : null}
        {stage.kind === "review" && !now ? (
          <Button variant="primary" disabled={busy} onClick={() => confirming.mutate(null)}>
            {portalText("subscription", "quote.confirm_change")}
          </Button>
        ) : null}
      </div>
      {opening.isPending ? <p className="pl-note" role="status">{portalText("subscription", "quote.opening_form")}</p> : null}
    </Block>
  );
}
